import * as DialogPrimitive from '@radix-ui/react-dialog'
import {
  ArrowDownLeftIcon,
  ArrowUpRightIcon,
  CheckCircle2Icon,
  TriangleAlertIcon,
  XIcon,
} from 'lucide-react'
import { Suspense, useEffect, useRef, useState } from 'react'

import {
  Button,
  DialogDescription,
  DialogOverlay,
  DialogTitle,
} from '@/components/ui'
import { useToast } from '@/hooks'
import {
  type Check,
  type CheckDirection,
  useCheckTransactions,
  useProjectMutations,
} from '@/modules/projects'
import { useDialogActions, useDialogData, useDialogIsOpen } from '@/stores'
import { cn, formatDate, numberToCurrency } from '@/utils'
import { LazyPdfViewer } from './LazyPdfViewer'
import type { PdfViewerHandle } from './PdfViewer'

const dialogId = 'check-review-workspace'

interface WorkspaceTarget {
  // Optional check to focus first; otherwise the worklist focuses the first flagged check.
  checkId?: number
}

const isFlagged = (check: Check) => check.needsReview

// Checks with no readable date sort after dated ones. lukita can send a null date.
const byDateUndatedLast = (first: Check, second: Check) => {
  if (!first.date || !second.date) {
    return Number(!first.date) - Number(!second.date)
  }
  return first.date.localeCompare(second.date)
}

// A stable worklist order: oldest first, then id so ties never reshuffle between renders.
const byReviewOrder = (first: Check, second: Check) =>
  byDateUndatedLast(first, second) || first.id - second.id

const DetailRow = ({ label, value }: { label: string; value: string }) => (
  <div className="flex items-baseline justify-between gap-4 py-0.5">
    <span className="text-xs text-muted-foreground">{label}</span>
    <span className="text-xs text-foreground tabular-nums">{value}</span>
  </div>
)

// One check in the worklist with its In / Out choice. "In" is a deposit (positive), "Out" a payment
// (negative). lukita re-signs the amount and clears the review flag when a direction is set.
const CheckCard = ({
  check,
  active,
  onFocus,
  onResolve,
}: {
  check: Check
  active: boolean
  onFocus: () => void
  onResolve: (direction: CheckDirection) => void
}) => (
  <div
    className={cn(
      'rounded-lg border bg-white p-3 transition-shadow',
      active ? 'ring-2 ring-inset ring-primary/40' : 'hover:shadow-sm',
    )}
  >
    <div className="flex items-center justify-between gap-3">
      <button
        type="button"
        onClick={onFocus}
        title="Show this check on the statement"
        className="cursor-pointer text-sm font-medium text-foreground hover:underline"
      >
        {check.checkNumber ? `Check #${check.checkNumber}` : 'Check'}
      </button>
      <span className="text-lg font-semibold tabular-nums text-foreground">
        {numberToCurrency(Math.abs(check.amount))}
      </span>
    </div>

    <div className="mt-2 border-y py-1.5">
      <DetailRow
        label="Date"
        value={check.date ? formatDate(check.date) : '--'}
      />
      <DetailRow
        label="Payee / description"
        value={check.description || '--'}
      />
      <DetailRow label="Account" value={check.accountNumber || '--'} />
    </div>

    <div className="mt-2 flex items-center justify-between gap-2">
      {check.needsReview ? (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700">
          <TriangleAlertIcon className="size-3.5" />
          Needs a direction
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700">
          <CheckCircle2Icon className="size-3.5" />
          Resolved
        </span>
      )}

      <div className="inline-flex overflow-hidden rounded-md border">
        <button
          type="button"
          onClick={() => onResolve('deposit')}
          className={cn(
            'flex cursor-pointer items-center gap-1 px-3 py-1 text-sm font-medium text-emerald-700 transition-colors',
            check.direction === 'deposit'
              ? 'bg-emerald-50'
              : 'hover:bg-emerald-50',
          )}
        >
          <ArrowDownLeftIcon className="size-3.5" />
          In
        </button>
        <button
          type="button"
          onClick={() => onResolve('payment')}
          className={cn(
            'flex cursor-pointer items-center gap-1 border-l px-3 py-1 text-sm font-medium text-red-700 transition-colors',
            check.direction === 'payment' ? 'bg-red-50' : 'hover:bg-red-50',
          )}
        >
          <ArrowUpRightIcon className="size-3.5" />
          Out
        </button>
      </div>
    </div>
  </div>
)

export const CheckReviewWorkspace = () => {
  const open = useDialogIsOpen(dialogId)
  const target = useDialogData() as WorkspaceTarget | undefined
  const { closeDialog } = useDialogActions()
  const { updateCheck } = useProjectMutations()
  const { errorToast } = useToast()

  const checks = useCheckTransactions()
  const pdfRef = useRef<PdfViewerHandle>(null)

  // Snapshot the worklist when the dialog opens so resolved checks stay in the list instead of
  // vanishing and the order holds. Read checks through a ref so the effect only fires on open.
  const checksRef = useRef(checks)
  checksRef.current = checks
  const [queue, setQueue] = useState<number[]>([])
  // The check whose page the document pane is showing.
  const [activeId, setActiveId] = useState<number | null>(null)

  useEffect(() => {
    if (!open) return
    const ids = checksRef.current
      .filter(isFlagged)
      .sort(byReviewOrder)
      .map((check) => check.id)
    setQueue(ids)
    setActiveId(target?.checkId ?? ids[0] ?? null)
  }, [open, target?.checkId])

  const queueChecks = queue
    .map((id) => checks.find((check) => check.id === id))
    .filter((check): check is Check => Boolean(check))
  const active = checks.find((check) => check.id === activeId)
  const total = queueChecks.length
  const remaining = queueChecks.filter(isFlagged).length

  // Focus a check and move the document to its page. Done on click, not in an effect, because checks
  // can share a page so a page-number dependency would never re-fire. A check in another statement
  // remounts the viewer via its key, and initialPage lands it on the right page.
  const focus = (check: Check) => {
    const sameDocument = check.statementFileUrl === active?.statementFileUrl
    setActiveId(check.id)
    if (sameDocument && check.sourcePage != null) {
      pdfRef.current?.goToPage(check.sourcePage)
    }
  }

  const resolve = (check: Check, direction: CheckDirection) => {
    focus(check)
    updateCheck(check.id, { direction }).catch((error) =>
      errorToast('Failed to set check direction', error),
    )
  }

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(next) => !next && closeDialog()}
    >
      <DialogPrimitive.Portal>
        <DialogOverlay className="bg-black/40" />
        <DialogPrimitive.Content className="fixed inset-0 z-50 flex flex-col bg-background">
          <div className="flex items-center justify-between gap-3 border-b p-4">
            <div className="min-w-0 space-y-1.5">
              <DialogTitle className="text-base font-semibold text-foreground">
                Review check direction
              </DialogTitle>
              <DialogDescription className="sr-only">
                Set whether each flagged check is money in or money out
              </DialogDescription>
              <p className="text-sm text-muted-foreground">
                {remaining === 0
                  ? `All ${total} checks resolved`
                  : `${remaining} of ${total} still need a direction`}
              </p>
            </div>
            <DialogPrimitive.Close asChild>
              <Button theme="ghost" size="sm" isIcon className="cursor-pointer">
                <XIcon className="size-4" />
                <span className="sr-only">Close</span>
              </Button>
            </DialogPrimitive.Close>
          </div>

          <div className="flex min-h-0 flex-1">
            {/* Left pane: the source document, opened to the focused check's page. Clicking a check on
                the right jumps it here. */}
            <div className="w-1/2 border-r">
              {active?.statementFileUrl ? (
                <Suspense
                  fallback={
                    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                      Loading the document…
                    </div>
                  }
                >
                  <LazyPdfViewer
                    key={active.statementFileUrl}
                    ref={pdfRef}
                    url={active.statementFileUrl}
                    initialPage={active.sourcePage}
                    highlightRange={
                      active.sourcePage != null
                        ? { start: active.sourcePage, end: active.sourcePage }
                        : null
                    }
                  />
                </Suspense>
              ) : (
                <div className="flex h-full items-center justify-center p-4 text-sm text-muted-foreground">
                  No source document is attached to this check.
                </div>
              )}
            </div>

            {/* Right pane: the flagged checks to work through, each with its In / Out choice. */}
            <div className="w-1/2 space-y-3 overflow-auto p-4 scrollbar-thin scrollbar-thumb-neutral-300">
              {total === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  No checks need review.
                </p>
              ) : (
                queueChecks.map((check) => (
                  <CheckCard
                    key={check.id}
                    check={check}
                    active={check.id === activeId}
                    onFocus={() => focus(check)}
                    onResolve={(direction) => resolve(check, direction)}
                  />
                ))
              )}
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

CheckReviewWorkspace.id = dialogId

// The Checks tab banner that opens the worklist. Hidden when nothing needs review.
export const ChecksReviewBanner = () => {
  const checks = useCheckTransactions()
  const { openDialog } = useDialogActions()
  const count = checks.filter(isFlagged).length
  if (count === 0) return null

  const noun = count === 1 ? 'check needs' : 'checks need'
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
      <div className="flex items-center gap-2 text-sm font-semibold text-amber-900">
        <TriangleAlertIcon className="size-4 shrink-0" />
        <span>
          <strong>{count}</strong> {noun} a direction before the amounts can be
          trusted.
        </span>
      </div>
      <Button
        theme="outline"
        size="sm"
        className="shrink-0 cursor-pointer border-amber-300 text-amber-800 hover:bg-amber-100"
        onClick={() => openDialog(dialogId)}
      >
        Review {count === 1 ? 'check' : 'checks'}
      </Button>
    </div>
  )
}
